import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, PencilSimple } from '@phosphor-icons/react';
import { ProgressStepper } from '@/components/ProgressStepper';

/**
 * Premium Review Modal for corporate catering order.
 * Shown after user fills the form, before final submit.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void  (Back-to-edit / X)
 *  - onConfirm: () => Promise<void>  (fires actual API submit)
 *  - submitting: boolean
 *  - formData: { company_name, contact_person, event_name, event_date, event_time, delivery_address, city, state, zipcode, mobile, email }
 *  - cartDetails: { lines: [{id,image,name,quantity,price,subtotal}], totalMeals, subtotal, gst, total }
 *  - onEditDetails: () => void
 *  - onEditCart: () => void
 */
export const CateringReviewModal = ({ open, onClose, onConfirm, submitting, formData, cartDetails, onEditDetails, onEditCart }) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => !submitting && onClose()}
          data-testid="review-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', damping: 26, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl relative flex flex-col"
          >
            {/* Header */}
            <div className="relative px-6 md:px-8 pt-6 pb-4 border-b border-stone-100">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">Final Step</p>
                  <h3 className="text-2xl font-medium font-heading tracking-tight text-[#1C1917]">Review your order</h3>
                </div>
                <button
                  onClick={() => !submitting && onClose()}
                  disabled={submitting}
                  className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors disabled:opacity-40"
                  data-testid="review-close-btn"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>
              <ProgressStepper steps={['Cart', 'Details', 'Review', 'Success']} current={3} />
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6 space-y-6">
              {/* Event summary */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">Event Details</p>
                  <button
                    onClick={onEditDetails}
                    className="text-xs text-[#1A4331] font-semibold hover:underline flex items-center gap-1"
                    data-testid="review-edit-details"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                </div>
                <div className="bg-stone-50 rounded-2xl p-4 grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-stone-500">Company / Host</p><p className="font-semibold text-[#1C1917] truncate">{formData.company_name}</p></div>
                  <div><p className="text-xs text-stone-500">Contact</p><p className="font-semibold text-[#1C1917] truncate">{formData.contact_person}</p></div>
                  <div><p className="text-xs text-stone-500">Event</p><p className="font-semibold text-[#1C1917] truncate">{formData.event_name}</p></div>
                  <div><p className="text-xs text-stone-500">Date &amp; Time</p><p className="font-semibold text-[#1C1917]">{formData.event_date} · {formData.event_time}</p></div>
                  <div className="col-span-2"><p className="text-xs text-stone-500">Delivery</p><p className="font-semibold text-[#1C1917] leading-snug">{formData.delivery_address}, {formData.city}, {formData.state} – {formData.zipcode}</p></div>
                  <div className="col-span-2"><p className="text-xs text-stone-500">Contact</p><p className="font-semibold text-[#1C1917]">{formData.mobile} · {formData.email}</p></div>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500">Order Items ({cartDetails.totalMeals} meals)</p>
                  <button
                    onClick={onEditCart}
                    className="text-xs text-[#1A4331] font-semibold hover:underline flex items-center gap-1"
                    data-testid="review-edit-cart"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {cartDetails.lines.map((line) => (
                    <div key={line.id} className="flex items-center gap-3 bg-stone-50 rounded-2xl p-3">
                      <div className="w-11 h-11 rounded-xl bg-white overflow-hidden flex-shrink-0">
                        <img src={line.image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#1C1917] truncate">{line.name}</p>
                        <p className="text-xs text-stone-500">{line.quantity} × ₹{line.price}</p>
                      </div>
                      <p className="font-bold text-[#1A4331]">₹{line.subtotal.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gradient-to-br from-[#1A4331] to-[#2A5A44] rounded-2xl p-5 text-white">
                <div className="flex justify-between text-sm text-white/80 mb-1"><span>Subtotal</span><span>₹{cartDetails.subtotal.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm text-white/80 mb-3"><span>GST (5%)</span><span>₹{cartDetails.gst.toLocaleString()}</span></div>
                <div className="border-t border-white/15 pt-3 flex justify-between items-end">
                  <span className="font-semibold">Grand Total</span>
                  <span className="text-2xl font-bold font-heading tracking-tight">₹{cartDetails.total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="border-t border-stone-100 px-6 md:px-8 py-4 bg-stone-50/50 flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={onClose}
                disabled={submitting}
                className="flex-1 border border-stone-300 bg-white text-stone-700 px-6 py-3 rounded-full font-semibold hover:bg-stone-100 transition-all disabled:opacity-40"
                data-testid="review-back-btn"
              >
                Back to edit
              </button>
              <button
                onClick={onConfirm}
                disabled={submitting}
                className="flex-1 bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white px-6 py-3 rounded-full font-semibold hover:shadow-2xl hover:shadow-[#1A4331]/30 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="review-confirm-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>Confirm &amp; Submit</>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CateringReviewModal;
